// 家用面板 L2 時鐘小工具的天氣地點（docs/panel 07-dashboard §3.4）。只限主人。
//   - 目前地點：自動（依面板網路）或手動選的城市；下面顯示目前天氣（城市、溫度、高低溫）
//   - ［選擇城市］→ 搜尋 → 點一筆 → PUT settings {weather: manual}；手動時［改回自動］→ {mode: auto}
//   - 404 WEATHER_NO_PLACE（自動但找不到城市）、503（天氣 / 搜尋暫時不能用）、422、網路錯誤都寫一句，不白畫面
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getPanelSettings, getPanelWeather, manualWeatherFrom, parseWeatherSetting, placeDetail, putPanelWeather, searchWeatherPlaces, weatherErr,
  type PanelWeatherNow, type PanelWeatherSetting, type WeatherErr, type WeatherPlace,
} from './panels';

type Now = { kind: 'loading' } | { kind: 'ok'; v: PanelWeatherNow } | { kind: 'err'; err: WeatherErr };
type Search = { q: string; busy: boolean; results: WeatherPlace[] | null; err: WeatherErr | null };

const deg = (c: number) => `${Math.round(c)}°`;

export function PanelWeatherCard({ panelId }: { panelId: string }) {
  const { t } = useTranslation();
  const [setting, setSetting] = useState<PanelWeatherSetting | null>(null);
  const [loadErr, setLoadErr] = useState<WeatherErr | null>(null);
  const [now, setNow] = useState<Now>({ kind: 'loading' });
  const [search, setSearch] = useState<Search | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const loadNow = useCallback(() => {
    setNow({ kind: 'loading' });
    return getPanelWeather(panelId)
      .then((v) => setNow(v ? { kind: 'ok', v } : { kind: 'err', err: 'unavailable' }))
      .catch((e) => setNow({ kind: 'err', err: weatherErr(e) }));
  }, [panelId]);

  useEffect(() => {
    getPanelSettings(panelId).then((r) => setSetting(parseWeatherSetting(r))).catch((e) => setLoadErr(weatherErr(e)));
    void loadNow();
  }, [panelId, loadNow]);

  const save = async (w: PanelWeatherSetting) => {
    setBusy(true);
    setMsg(null);
    try {
      const saved = await putPanelWeather(panelId, w);
      setSetting(saved);
      setSearch(null);
      setMsg(saved.mode === 'manual' ? t('panels.weather.savedManual', { name: saved.name }) : t('panels.weather.savedAuto'));
      void loadNow();
    } catch (e) {
      setMsg(t(`panels.weather.err_${weatherErr(e)}`));
    } finally {
      setBusy(false);
    }
  };

  const runSearch = async (ev: FormEvent) => {
    ev.preventDefault();
    if (!search) return;
    const q = search.q.trim();
    if (!q) return;
    setSearch({ ...search, busy: true, err: null });
    try {
      const results = await searchWeatherPlaces(q);
      setSearch((s) => s && { ...s, busy: false, results });
    } catch (e) {
      const err = weatherErr(e);
      setSearch((s) => s && { ...s, busy: false, results: null, err: err === 'unavailable' ? 'unavailable' : err === 'network' ? 'network' : 'generic' });
    }
  };

  const manual = setting?.mode === 'manual';
  return (
    <div className="card dv-weather-card" data-testid="panel-weather">
      <h2>{t('panels.weather.title')}</h2>
      <p className="text-secondary">{t('panels.weather.hint')}</p>
      {loadErr && <p className="text-secondary" role="alert">{t(`panels.weather.err_${loadErr}`)}</p>}
      {setting && (
        <>
          <div className="dv-weather-row">
            <div className="dv-layout-meta">
              <span className="text-secondary">{t('panels.weather.place')}</span>
              <strong data-testid="weather-place">{manual ? setting.name : t('panels.weather.auto')}</strong>
            </div>
            <div className="dv-weather-actions">
              {!search && (
                <button className="btn-ghost" disabled={busy} onClick={() => { setMsg(null); setSearch({ q: '', busy: false, results: null, err: null }); }}>
                  {t('panels.weather.pick')}
                </button>
              )}
              {manual && (
                <button className="btn-ghost" disabled={busy} onClick={() => void save({ mode: 'auto' })} data-testid="weather-auto">
                  {t('panels.weather.backToAuto')}
                </button>
              )}
            </div>
          </div>

          <div className="dv-weather-now" data-testid="weather-now" aria-live="polite">
            {now.kind === 'loading' && <span className="text-secondary">{t('panels.weather.loading')}</span>}
            {now.kind === 'ok' && (
              <>
                <span className="dv-weather-temp">{deg(now.v.temp_c)}</span>
                <span className="dv-layout-meta">
                  <span>{now.v.place}</span>
                  <span className="text-secondary">{t('panels.weather.hiLo', { hi: deg(now.v.hi_c), lo: deg(now.v.lo_c) })}</span>
                </span>
              </>
            )}
            {now.kind === 'err' && <span className="text-secondary">{t(`panels.weather.now_${now.err === 'noPlace' ? 'noPlace' : now.err === 'unavailable' ? 'unavailable' : 'err'}`)}</span>}
          </div>

          {search && (
            <div className="dv-weather-search" data-testid="weather-search">
              <form role="search" className="dv-weather-form" onSubmit={(e) => void runSearch(e)}>
                <input type="search" value={search.q} maxLength={60} autoFocus aria-label={t('panels.weather.searchLabel')}
                  placeholder={t('panels.weather.searchPlaceholder')} onChange={(e) => setSearch({ ...search, q: e.target.value })} />
                <button type="submit" className="btn-primary" disabled={search.busy || !search.q.trim()}>{t('panels.weather.search')}</button>
                <button type="button" className="btn-ghost" onClick={() => setSearch(null)}>{t('common.cancel')}</button>
              </form>
              {search.busy && <p className="text-secondary">{t('panels.weather.searching')}</p>}
              {search.err && <p className="text-secondary" role="alert">{t(`panels.weather.searchErr_${search.err}`)}</p>}
              {search.results && search.results.length === 0 && <p className="text-secondary">{t('panels.weather.noResults')}</p>}
              {search.results && search.results.length > 0 && (
                <ul className="dv-layout-list" aria-label={t('panels.weather.results')}>
                  {search.results.map((p, i) => (
                    <li key={`${p.lat},${p.lon},${i}`}>
                      <button className="dv-weather-result" disabled={busy} onClick={() => void save(manualWeatherFrom(p))} data-testid={`weather-result-${i}`}>
                        <span>{p.name}</span>
                        {placeDetail(p) && <span className="text-secondary">{placeDetail(p)}</span>}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
      {msg && <p className="text-secondary" role="status">{msg}</p>}
    </div>
  );
}
